// Generic CSV → canonical corpus.csv.
//
// Direct port of evals/adapters/generic_csv.py. Browser users uploading
// a CSV (not a PDF) get validation + canonicalization here instead of
// shipping a possibly-malformed file to the cloud-server. The browser
// catches missing columns + bad timestamps in-process; the user fixes
// the file BEFORE we round-trip to the server.
//
// Required input columns: timestamp, sender, recipient, body, attachments.
// Output: canonical six-column CSV (same shape as OFW + CC):
//   timestamp, sender, recipient, subject, body, attachments  (subject="")
//
// Rejection rules match the Python adapter:
// - Invalid timestamp (non-ISO, missing timezone) → reject row, count
//   by_reason.invalid_timestamp.
// - Empty sender or empty body → reject row, count
//   by_reason.missing_required_field.
// - Empty recipient is allowed (group threads).
import { DateTime, IANAZone } from "luxon";

import { sanitizeActor, sanitizeBody } from "./_normalize";
import {
  isComplete as mappingIsComplete,
  projectRows,
  type ColumnMapping,
} from "./_column_mapping";

export const PARSER_VERSION = "0.1.0";
export const ADAPTER_NAME = "generic-csv";

const REQUIRED_COLUMNS = [
  "timestamp",
  "sender",
  "recipient",
  "body",
  "attachments",
] as const;
const CANONICAL_HEADERS = [
  "timestamp",
  "sender",
  "recipient",
  "subject",
  "body",
  "attachments",
] as const;

export class MalformedGenericCsvError extends Error {
  constructor(msg: string) {
    super(msg);
    this.name = "MalformedGenericCsvError";
  }
}

// --- Types ---

export interface CanonicalRow {
  timestamp: string;
  sender: string;
  recipient: string;
  subject: string;
  body: string;
  attachments: string;
}

export interface IngestStats {
  party_a: string;
  party_b: string;
  out_of_set_senders: string[];
  message_count: number;
  accepted_count: number;
  rejected_count: number;
  non_printables_stripped: number;
  date_range: { start: string | null; end: string | null };
  by_reason: Record<string, number>;
}

export interface CaseMeta {
  id: string;
  privacy_class: string;
  prompt_vars: {
    party_a: string;
    party_b: string;
    format: string;
    time_period: string;
    context: string;
  };
  known_patterns: string[];
  notes: string;
}

export interface IngestResult {
  rows: CanonicalRow[];
  stats: IngestStats;
  csv: string;
  case_meta: CaseMeta;
  warnings: string[];
}

// --- CSV parsing ---

/**
 * Parse a CSV string into header + row records. Handles quoted cells +
 * doubled-quote escapes per RFC 4180. Newlines inside quoted cells are
 * preserved as part of the cell value.
 */
export function parseCsvText(text: string): { header: string[]; rows: Record<string, string>[] } {
  // Strip BOM if present.
  if (text.charCodeAt(0) === 0xfeff) {
    text = text.slice(1);
  }
  const cells: string[][] = [[]];
  let cur = "";
  let inQuotes = false;
  let i = 0;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          cur += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      cur += c;
      i += 1;
      continue;
    }
    if (c === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (c === ",") {
      cells[cells.length - 1].push(cur);
      cur = "";
      i += 1;
      continue;
    }
    if (c === "\r") {
      // Lookahead for \r\n; otherwise treat \r as line terminator.
      if (text[i + 1] === "\n") {
        i += 2;
      } else {
        i += 1;
      }
      cells[cells.length - 1].push(cur);
      cur = "";
      cells.push([]);
      continue;
    }
    if (c === "\n") {
      cells[cells.length - 1].push(cur);
      cur = "";
      cells.push([]);
      i += 1;
      continue;
    }
    cur += c;
    i += 1;
  }
  // Flush the final cell + drop a trailing empty row from a terminal newline.
  cells[cells.length - 1].push(cur);
  if (
    cells.length > 0 &&
    cells[cells.length - 1].length === 1 &&
    cells[cells.length - 1][0] === ""
  ) {
    cells.pop();
  }
  if (cells.length === 0) {
    throw new MalformedGenericCsvError("CSV is empty");
  }
  const header = cells[0];
  const rows: Record<string, string>[] = [];
  for (let r = 1; r < cells.length; r++) {
    const obj: Record<string, string> = {};
    for (let c = 0; c < header.length; c++) {
      obj[header[c]] = cells[r][c] ?? "";
    }
    rows.push(obj);
  }
  return { header, rows };
}

// --- Timestamp validation ---

/**
 * Strict ISO-8601 timestamp parser. Returns the canonical UTC string in
 * "YYYY-MM-DDTHH:MM:SSZ" form, or null on rejection. Rejects:
 * - empty
 * - timestamps without a timezone offset (naive datetimes — forensic
 *   clarity requires explicit timezone)
 * - any non-ISO-8601 shape
 *
 * MUST match the desktop / server adapter (`evals/adapters/generic_csv.py`
 * `_parse_timestamp_strict`, which uses `datetime.fromisoformat`) so the
 * browser preview accepts exactly the rows the authoritative server ingest
 * accepts. We deliberately do NOT use a bare `Date.parse` (it leniently
 * accepts RFC-2822 / locale strings like "Jan 15 2024 10:30 GMT+0500" and
 * whitespace-padded input that `fromisoformat` rejects), and we do NOT trim
 * — Python rejects surrounding whitespace, so we must too. Instead we gate
 * on a strict ISO-8601 shape, then hand a normalized form to Date.parse.
 */
function parseTimestampStrict(raw: string): string | null {
  if (!raw) return null;
  // Date + ('T' or space) separator + HH:MM[:SS][.frac] + required offset
  // (Z or ±HH:MM[:SS]). A date-only value is naive → rejected (needs tz).
  const m =
    /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?)(Z|[+-]\d{2}:\d{2}(?::\d{2})?)$/.exec(
      raw,
    );
  if (!m) return null;
  const offset = m[3] === "Z" ? "+00:00" : m[3];
  const ms = Date.parse(`${m[1]}T${m[2]}${offset}`);
  if (Number.isNaN(ms)) return null;
  const d = new Date(ms);
  // Format as UTC ISO with second precision + Z suffix (mirrors Python's
  // .astimezone(utc).isoformat(timespec="seconds")).
  return d.toISOString().replace(/\.\d{3}Z$/, "Z");
}

// --- CSV serialization ---

function csvEscape(cell: string): string {
  if (/[",\n\r]/.test(cell)) {
    return `"${cell.replace(/"/g, '""')}"`;
  }
  return cell;
}

function rowsToCanonicalCsv(rows: CanonicalRow[]): string {
  const header = CANONICAL_HEADERS.join(",") + "\n";
  if (rows.length === 0) return header;
  const body = rows
    .map((r) => CANONICAL_HEADERS.map((h) => csvEscape(r[h])).join(","))
    .join("\n");
  return header + body + "\n";
}

// --- Case meta + party derivation ---

function deriveParties(
  rows: CanonicalRow[],
): { party_a: string; party_b: string; out_of_set: string[] } {
  if (rows.length === 0) {
    return { party_a: "", party_b: "", out_of_set: [] };
  }
  const counts = new Map<string, number>();
  const firstSeen = new Map<string, number>();
  rows.forEach((r, idx) => {
    if (!r.sender) return;
    counts.set(r.sender, (counts.get(r.sender) ?? 0) + 1);
    if (!firstSeen.has(r.sender)) firstSeen.set(r.sender, idx);
  });
  const ordered = [...counts.entries()].sort((a, b) => {
    if (b[1] !== a[1]) return b[1] - a[1];
    return (firstSeen.get(a[0]) ?? 0) - (firstSeen.get(b[0]) ?? 0);
  });
  const party_a = ordered[0]?.[0] ?? "";
  const party_b = ordered[1]?.[0] ?? "";
  const declared = new Set([party_a, party_b]);
  const out_of_set = [...counts.keys()].filter((n) => !declared.has(n)).sort();
  return { party_a, party_b, out_of_set };
}

const TODO_CONTEXT_SENTINEL =
  "TODO: describe the context (e.g. 'their two children')";
const TODO_NOTES_SENTINEL = "TODO: optional freeform notes about this corpus";

function formatTimePeriod(startIso: string, endIso: string): string {
  if (!startIso || !endIso) return "";
  const fmt = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const month = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
    return `${month} ${d.getUTCFullYear()}`;
  };
  const a = fmt(startIso);
  const b = fmt(endIso);
  if (!a || !b) return "";
  return a === b ? a : `${a} – ${b}`;
}

function buildCaseMeta(corpusId: string, stats: IngestStats): CaseMeta {
  return {
    id: corpusId,
    privacy_class: "private",
    prompt_vars: {
      party_a: stats.party_a,
      party_b: stats.party_b,
      format:
        "Generic CSV (timestamp/sender/recipient/body/attachments, ISO-8601)",
      time_period: formatTimePeriod(
        stats.date_range.start ?? "",
        stats.date_range.end ?? "",
      ),
      context: TODO_CONTEXT_SENTINEL,
    },
    known_patterns: [],
    notes: TODO_NOTES_SENTINEL,
  };
}

// --- Public entry point ---

export interface IngestOptions {
  corpusId: string;
}

/**
 * Ingest a generic CSV into the canonical CSV shape. Validates required
 * columns + per-row timestamp + required fields. Throws on
 * missing-required-columns or empty-file. Per-row rejections are
 * counted but do not abort the ingest (mirrors the Python adapter).
 */
export function ingestGenericCsv(
  text: string,
  opts: IngestOptions,
): IngestResult {
  const parsed = parseCsvText(text);
  if (parsed.rows.length === 0 && parsed.header.length === 0) {
    throw new MalformedGenericCsvError("CSV is empty");
  }
  const missing = REQUIRED_COLUMNS.filter((c) => !parsed.header.includes(c));
  if (missing.length > 0) {
    throw new MalformedGenericCsvError(
      `CSV missing required columns: ${missing.join(", ")}. Required: ${REQUIRED_COLUMNS.join(", ")}.`,
    );
  }

  const byReason: Record<string, number> = {};
  let nonPrintablesStripped = 0;
  const acceptedRows: CanonicalRow[] = [];
  const warnings: string[] = [];

  for (const row of parsed.rows) {
    const ts = parseTimestampStrict(row.timestamp ?? "");
    if (ts === null) {
      byReason.invalid_timestamp = (byReason.invalid_timestamp ?? 0) + 1;
      continue;
    }
    const [sender, nSender] = sanitizeActor(row.sender ?? "");
    nonPrintablesStripped += nSender;
    if (!sender) {
      byReason.missing_required_field = (byReason.missing_required_field ?? 0) + 1;
      continue;
    }
    const [body, nBody] = sanitizeBody(row.body ?? "");
    nonPrintablesStripped += nBody;
    if (!body) {
      byReason.missing_required_field = (byReason.missing_required_field ?? 0) + 1;
      continue;
    }
    const [recipient, nRecip] = sanitizeActor(row.recipient ?? "");
    nonPrintablesStripped += nRecip;
    const attachments = row.attachments ?? "";

    acceptedRows.push({
      timestamp: ts,
      sender,
      recipient,
      subject: "",
      body,
      attachments,
    });
  }

  // Sort by timestamp ascending — forensic convention; consumers may rely on it.
  acceptedRows.sort((a, b) =>
    a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0,
  );

  const { party_a, party_b, out_of_set } = deriveParties(acceptedRows);
  const dateRange = {
    start: acceptedRows.length > 0 ? acceptedRows[0].timestamp : null,
    end:
      acceptedRows.length > 0
        ? acceptedRows[acceptedRows.length - 1].timestamp
        : null,
  };

  const stats: IngestStats = {
    party_a,
    party_b,
    out_of_set_senders: out_of_set,
    message_count: acceptedRows.length,
    accepted_count: acceptedRows.length,
    rejected_count: Object.values(byReason).reduce((s, n) => s + n, 0),
    non_printables_stripped: nonPrintablesStripped,
    date_range: dateRange,
    by_reason: byReason,
  };

  if (stats.rejected_count > 0) {
    const reasons = Object.entries(byReason)
      .map(([k, v]) => `${k}=${v}`)
      .join(", ");
    warnings.push(
      `${stats.rejected_count} of ${stats.rejected_count + stats.accepted_count} rows rejected (${reasons})`,
    );
  }

  const csv = rowsToCanonicalCsv(acceptedRows);
  const case_meta = buildCaseMeta(opts.corpusId, stats);

  return {
    rows: acceptedRows,
    stats,
    csv,
    case_meta,
    warnings,
  };
}

// =====================================================================
// Column-mapping path (Phase 3, Piece B — spec §9). Lockstep with
// evals/adapters/generic_csv.py parse_generic_csv(column_map=...).
//
// An arbitrary tabular CSV whose headers were fuzzy-mapped to roles. We project
// rows to canonical, parse the mapped timestamp column FLEXIBLY (naive values
// localized with the case zone), and emit a canonical CSV (ISO-Z timestamps)
// the server re-validates on reindex. Composite multi-column timestamps are out
// of scope here (use the OFW-CSV adapter); see BACKLOG.
// =====================================================================

/**
 * Parse a timestamp in a flexible (non-strict) shape, returning the canonical
 * UTC ISO string or null. Offset-aware inputs convert to UTC (the offset wins);
 * naive inputs are localized to `assumeZone` and converted, or return null when
 * no zone is assigned. Uses Luxon's ISO / RFC2822 / HTTP / SQL parsers plus a
 * permissive Date fallback so common spreadsheet shapes ("Jan 2 2024 9:30am",
 * "01/02/2024 09:30") resolve. `wasNaive` mirrors the Python flag.
 */
export function parseTimestampFlexible(
  raw: string,
  assumeZone: string | null,
): { iso: string | null; wasNaive: boolean } {
  const s = (raw ?? "").trim();
  if (!s) return { iso: null, wasNaive: false };

  // A string carries its own zone iff it has a Z/±HH:MM offset or a named
  // UTC/GMT token; otherwise it's a naive wall-clock that needs assumeZone.
  const hasExplicitZone =
    /[Zz]$|[+-]\d{2}:?\d{2}$/.test(s) || /\b(?:GMT|UTC)\b/i.test(s);

  // When the string DOES carry a zone, parse it zone-aware (offset wins).
  if (hasExplicitZone) {
    let dt = DateTime.fromISO(s, { setZone: true });
    if (!dt.isValid) dt = DateTime.fromRFC2822(s, { setZone: true });
    if (!dt.isValid) {
      const ms = Date.parse(s);
      if (Number.isNaN(ms)) return { iso: null, wasNaive: false };
      dt = DateTime.fromMillis(ms, { zone: "utc" });
    }
    if (!dt.isValid) return { iso: null, wasNaive: false };
    return { iso: dt.toUTC().toFormat("yyyy-MM-dd'T'HH:mm:ss'Z'"), wasNaive: false };
  }

  // Naive — no zone to place it in.
  if (assumeZone === null) return { iso: null, wasNaive: true };

  // Parse the wall-clock components, then localize to assumeZone. Try Luxon's
  // ISO / SQL parsers (zone:"utc" reads the wall-clock at UTC, i.e. literal
  // components), then a permissive fallback for free-form shapes. NOTE: the
  // free-form fallback (Date.parse) is more conservative than the desktop's
  // dateutil and may REJECT a few exotic free-form shapes the desktop accepts.
  // For rows both engines accept, the emitted ISO-Z is byte-identical, so the
  // analyzed corpus matches desktop. The divergence is only at the rejection
  // boundary: a shape the cloud rejects (→ surfaced as a rejected row in the
  // preview, not silently dropped) might have been accepted on desktop. This
  // never affects the OFW CSV (fixed format, no free-form path). Tracked in
  // BACKLOG (cloud-vs-desktop generic timestamp parser parity).
  const zone = new IANAZone(assumeZone);
  let dt = DateTime.fromISO(s, { zone: "utc" });
  if (!dt.isValid) dt = DateTime.fromSQL(s, { zone: "utc" });
  let comps: { year: number; month: number; day: number; hour: number; minute: number; second: number } | null = null;
  if (dt.isValid) {
    comps = {
      year: dt.year, month: dt.month, day: dt.day,
      hour: dt.hour, minute: dt.minute, second: dt.second,
    };
  } else {
    // Free-form ("Jan 2 2024 9:30am"). Date.parse reads it as LOCAL; pull the
    // local wall-clock components back out (the zone offset cancels).
    const ms = Date.parse(s);
    if (Number.isNaN(ms)) return { iso: null, wasNaive: true };
    const d = new Date(ms);
    comps = {
      year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(),
      hour: d.getHours(), minute: d.getMinutes(), second: d.getSeconds(),
    };
  }
  const localized = DateTime.fromObject(comps, { zone });
  if (!localized.isValid) return { iso: null, wasNaive: true };
  return {
    iso: localized.toUTC().toFormat("yyyy-MM-dd'T'HH:mm:ss'Z'"),
    wasNaive: true,
  };
}

export interface MappedIngestStats extends IngestStats {
  needs_timezone: boolean;
  input_timezone: string | null;
  zone_source: string | null;
}

export interface MappedIngestOptions {
  corpusId: string;
  /** The user's explicit IANA zone for naive timestamps (Piece C). */
  inputTimezone?: string;
}

/**
 * Ingest an arbitrary tabular CSV via a confirmed column→role mapping. Produces
 * a canonical CSV (ISO-Z timestamps) ready to upload; the server re-validates.
 */
export function ingestGenericCsvMapped(
  text: string,
  mapping: ColumnMapping,
  opts: MappedIngestOptions,
): IngestResult & { stats: MappedIngestStats } {
  if (!mappingIsComplete(mapping)) {
    throw new MalformedGenericCsvError(
      "Column mapping is incomplete — the message, who sent it, and when must all be chosen.",
    );
  }
  const parsed = parseCsvText(text);
  const projected = projectRows(parsed.rows, mapping);

  const assumeZone = opts.inputTimezone ?? null;
  const byReason: Record<string, number> = {};
  let nonPrintablesStripped = 0;
  let naiveCount = 0;
  const acceptedRows: CanonicalRow[] = [];
  const warnings: string[] = [];

  for (const row of projected) {
    const { iso, wasNaive } = parseTimestampFlexible(row.timestamp, assumeZone);
    if (wasNaive) naiveCount += 1;
    if (iso === null) {
      const reason = wasNaive ? "missing_timezone" : "invalid_timestamp";
      byReason[reason] = (byReason[reason] ?? 0) + 1;
      continue;
    }
    const [sender, nS] = sanitizeActor(row.sender);
    nonPrintablesStripped += nS;
    if (!sender) {
      byReason.missing_required_field = (byReason.missing_required_field ?? 0) + 1;
      continue;
    }
    const [body, nB] = sanitizeBody(row.body);
    nonPrintablesStripped += nB;
    if (!body) {
      byReason.missing_required_field = (byReason.missing_required_field ?? 0) + 1;
      continue;
    }
    const [recipient, nR] = sanitizeActor(row.recipient);
    nonPrintablesStripped += nR;
    const [subject, nSubj] = sanitizeBody(row.subject);
    nonPrintablesStripped += nSubj;
    acceptedRows.push({
      timestamp: iso,
      sender,
      recipient,
      subject,
      body,
      attachments: "",
    });
  }

  acceptedRows.sort((a, b) =>
    a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0,
  );

  const { party_a, party_b, out_of_set } = deriveParties(acceptedRows);
  const dateRange = {
    start: acceptedRows.length > 0 ? acceptedRows[0].timestamp : null,
    end: acceptedRows.length > 0 ? acceptedRows[acceptedRows.length - 1].timestamp : null,
  };
  const needsTimezone = naiveCount > 0 && assumeZone === null;
  const stats: MappedIngestStats = {
    party_a,
    party_b,
    out_of_set_senders: out_of_set,
    message_count: acceptedRows.length,
    accepted_count: acceptedRows.length,
    rejected_count: Object.values(byReason).reduce((s, n) => s + n, 0),
    non_printables_stripped: nonPrintablesStripped,
    date_range: dateRange,
    by_reason: byReason,
    needs_timezone: needsTimezone,
    input_timezone: assumeZone,
    zone_source: assumeZone !== null && naiveCount > 0 ? "user_assigned" : null,
  };

  return {
    rows: acceptedRows,
    stats,
    csv: rowsToCanonicalCsv(acceptedRows),
    case_meta: buildCaseMeta(opts.corpusId, stats),
    warnings,
  };
}
